import { useRef, useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Camera, X, Tag, Gift } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PasswordInput } from "@/components/common/PasswordInput";
import { PasswordStrength } from "@/components/common/PasswordStrength";
import { useAuth, apiError } from "@/context/AuthContext";
import { fileToResizedDataUrl } from "@/lib/image";
import { setStoredPromo, getStoredPromo } from "@/lib/promos";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const fileRef = useRef(null);
    const promo = (params.get("code") || getStoredPromo() || "").toUpperCase();
    const ref = (params.get("ref") || "").toUpperCase();
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", terms_accepted: false });
    const [avatar, setAvatar] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => { if (promo) setStoredPromo(promo); }, [promo]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const pickAvatar = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const dataUrl = await fileToResizedDataUrl(file, 256, 0.82);
            setAvatar(dataUrl);
        } catch (err) { toast.error(err.message || "Could not load image."); }
        finally { e.target.value = ""; }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
            setError("Password must be at least 8 characters and include letters and numbers.");
            return;
        }
        if (!form.terms_accepted) {
            setError("You must accept the Terms of Service and Privacy Policy.");
            return;
        }
        setLoading(true); setError("");
        try {
            await register({ ...form, avatar_url: avatar || undefined, ref: ref || undefined });
            sessionStorage.setItem("ww_onboard", "1");
            toast.success("Account created — let's start your free trial.");
            navigate("/onboarding");
        } catch (err) {
            setError(apiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const initials = `${form.first_name[0] || ""}${form.last_name[0] || ""}`.toUpperCase() || "WW";

    return (
        <AuthLayout title="Create account" subtitle="Start coaching every session in seconds.">
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
                {promo && (
                    <div className="flex items-center gap-2.5 rounded-sm border border-volt/40 bg-volt/[0.08] px-3.5 py-2.5" data-testid="register-promo-banner">
                        <Tag className="w-4 h-4 text-volt shrink-0" />
                        <p className="text-sm text-white">
                            Promo <span className="font-mono font-bold text-volt">{promo}</span> locked in — applied automatically at checkout.
                        </p>
                    </div>
                )}
                {ref && (
                    <div className="flex items-center gap-2.5 rounded-sm border border-success/40 bg-success/[0.08] px-3.5 py-2.5" data-testid="register-ref-banner">
                        <Gift className="w-4 h-4 text-success shrink-0" />
                        <p className="text-sm text-white">
                            Buddy code <span className="font-mono font-bold text-success">{ref}</span> applied — you'll both earn bonus WhiteWater Coins.
                        </p>
                    </div>
                )}
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <Avatar className="w-16 h-16 rounded-sm border border-border">
                            <AvatarImage src={avatar} />
                            <AvatarFallback className="rounded-sm bg-volt/15 text-volt font-bold">{initials}</AvatarFallback>
                        </Avatar>
                        {avatar && (
                            <button type="button" onClick={() => setAvatar("")} data-testid="register-avatar-clear"
                                className="absolute -top-1.5 -right-1.5 grid place-items-center w-5 h-5 rounded-full bg-alert text-white"><X className="w-3 h-3" /></button>
                        )}
                    </div>
                    <div>
                        <button type="button" onClick={() => fileRef.current?.click()} data-testid="register-avatar-btn"
                            className="h-9 px-4 rounded-sm border border-border hover:bg-white/5 text-sm flex items-center gap-2">
                            <Camera className="w-4 h-4 text-volt" /> Upload Photo
                        </button>
                        <div className="text-[11px] text-muted-foreground mt-1">Optional · JPG/PNG</div>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickAvatar} data-testid="register-avatar-input" />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">First Name</Label>
                        <Input data-testid="register-first-name" required value={form.first_name} onChange={set("first_name")}
                            placeholder="Alex" className="bg-surface border-border rounded-sm h-11" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Last Name</Label>
                        <Input data-testid="register-last-name" required value={form.last_name} onChange={set("last_name")}
                            placeholder="Rivera" className="bg-surface border-border rounded-sm h-11" />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                    <Input data-testid="register-email" type="email" required value={form.email} onChange={set("email")}
                        placeholder="you@example.com" className="bg-surface border-border rounded-sm h-11" />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                    <PasswordInput testid="register-password" value={form.password} onChange={set("password")}
                        autoComplete="new-password" placeholder="Create a strong password" />
                    <PasswordStrength value={form.password} />
                </div>
                <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={form.terms_accepted}
                        onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })}
                        className="mt-0.5 accent-[#00C2FF]" data-testid="register-terms" />
                    <span>I agree to the <Link to="/terms" className="text-volt hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-volt hover:underline">Privacy Policy</Link>.</span>
                </label>
                {error && <div className="text-sm text-alert font-mono" data-testid="register-error">{error}</div>}
                <button data-testid="register-submit" disabled={loading} type="submit"
                    className="w-full h-11 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
                </button>
                <p className="text-sm text-muted-foreground text-center">
                    Already have an account? <Link to="/login" className="text-volt hover:underline" data-testid="login-link">Sign in</Link>
                </p>
            </form>
        </AuthLayout>
    );
}
